import type { StoreApi } from "zustand";

import { withPipelineConfigSync } from "../../../../common/stores/pipelineConfigSyncStore";
import {
  createAnalysisProfile,
  deleteAnalysisProfile,
  fetchAnalysisProfiles,
  updateAnalysisProfile
} from "../../../../data/queries/accountAnalysis";
import { AnalysisProfile } from "../../types";
import type { AccountAnalysisStore } from "../accountAnalysisStore";

export interface ProfileActions {
  // Sync actions
  setProfiles: (profiles: AnalysisProfile[]) => void;
  setSelectedProfileIds: (ids: string[]) => void;
  setIsLoadingProfiles: (loading: boolean) => void;
  setProfilesError: (error: string | null) => void;

  // Async actions
  fetchProfiles: () => Promise<void>;
  addProfile: (profile: Omit<AnalysisProfile, "id" | "created" | "modified" | "deleted">) => Promise<void>;
  updateProfile: (id: string, updates: Partial<AnalysisProfile>) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;
}

export const createProfileSlice = (
  set: StoreApi<AccountAnalysisStore>["setState"],
  get: StoreApi<AccountAnalysisStore>["getState"]
): ProfileActions => {
  const synced = withPipelineConfigSync(get);

  return {
  // Sync actions
    setProfiles: (profiles) => set({ profiles }),
    setSelectedProfileIds: (ids) => set({ selectedProfileIds: ids }),
    setIsLoadingProfiles: (isLoadingProfiles) => set({ isLoadingProfiles }),
    setProfilesError: (profilesError) => set({ profilesError }),

    // Async actions
    fetchProfiles: async () => {
      set({ isLoadingProfiles: true, profilesError: null });
      try {
        const profiles = await fetchAnalysisProfiles();
        set({ profiles, isLoadingProfiles: false });
      } catch (error) {
        set({
          profilesError: error instanceof Error ? error.message : "Failed to fetch profiles",
          isLoadingProfiles: false
        });
      }
    },

    addProfile: synced(async (profileData) => {
      try {
        const profile = await createAnalysisProfile(profileData);
        set((state: AccountAnalysisStore) => ({ profiles: [profile, ...state.profiles] }));
      } catch (error) {
        set({
          profilesError: error instanceof Error ? error.message : "Failed to create profile"
        });
        throw error;
      }
    }),

    updateProfile: synced(async (id, updates) => {
      try {
        const updatedProfile = await updateAnalysisProfile(id, updates);
        set((state: AccountAnalysisStore) => ({
          profiles: state.profiles.map((p) =>
            p.id === id ? updatedProfile : p
          )
        }));
      } catch (error) {
        set({
          profilesError: error instanceof Error ? error.message : "Failed to update profile"
        });
        throw error;
      }
    }),

    deleteProfile: synced(async (id) => {
      try {
        await deleteAnalysisProfile(id);
        set((state: AccountAnalysisStore) => ({
          profiles: state.profiles.filter((p) => p.id !== id)
        }));
      } catch (error) {
        set({
          profilesError: error instanceof Error ? error.message : "Failed to delete profile"
        });
        throw error;
      }
    })
  };
};
